import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { DomSanitizer, Meta } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription, Observable, combineLatest, BehaviorSubject, of, EMPTY } from 'rxjs';
import { map, switchMap, filter, shareReplay, startWith, take, distinctUntilChanged } from 'rxjs/operators';

// Redux
import { setBrowserTitle, dispatchAppError, dispatchInfoMessage, openWindow } from 'src/app/actions/core.actions';
import * as BookmarkProblemListActions from 'src/app/actions/bookmark-problem-list.actions';
import * as BookmarkProblemsActions from 'src/app/actions/bookmark-problems.actions';
import { findStaticData, initializeStaticDataState } from 'src/app/actions/static-data.actions';
import {
  findScienceProblemIds,
  findScienceProblem,
  findEnglishProblemIds,
  findEnglishProblem,
  findNationalLanguageProblemIds,
  findNationalLanguageProblemsById,
  findHistoryProblemIds,
  findHistoryProblem,
  findProblemIdsByFreeword
} from 'src/app/actions/search.actions';
import { findBookmarkProblemIds } from 'src/app/actions/bookmark.actions';
import * as BookmarkActions from 'src/app/actions/bookmark.actions';
import { RootState } from 'src/app/reducers';
import { getFetchedDate } from 'src/app/selectors/static-data.selectors';
import {
  getMatchedProblemIds,
  getScienceProblem,
  getEnglishProblem,
  getNationalLanguageProblemsWithSameId,
  getHistoryProblem,
  getReadableMathProblem,
  getProblemSearching,
  getProblemIdsSearching,
  getFirstProblemIdFromMatchedProblemIds,
  getLastProblemIdFromMatchedProblemIds,
  getReadablePhysicsProblem,
  getReadableChemistryProblem,
  getReadableBiologyProblem,
  getReadableEnglishProblem,
  getReadableNationalLanguageProblemsFromProblems,
  getReadableJapaneseHistoryProblem,
  getReadableWorldHistoryProblem,
  getReadableGeographyProblem,
  getReadablePoliticalEconomyProblem,
  getReadableSpecialAdmissionsProblem
} from 'src/app/selectors/search.selectors';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import * as BookmarkSelectors from 'src/app/selectors/bookmark.selectors';
import * as BookmarkProblemListSelectors from 'src/app/selectors/bookmark-problem-list.selectors';
import * as StaticDataSelectors from '../../../selectors/static-data.selectors';

// Mapper
import { QueryParamsMapper } from 'src/app/mappers/query-params-mapper';
import { ReadableDataMapper } from 'src/app/mappers/readable-data-mapper';

// Models
import {
  ScienceProblem,
  ReadableScienceProblem,
  EnglishProblem,
  ReadableEnglishProblem,
  NationalLanguageProblem,
  ReadableNationalLanguageProblem,
  HistoryProblem,
  ReadableHistoryProblem,
  Problem
} from 'src/app/models/problem';
import { SearchProblemsCondition } from 'src/app/models/search-condition';
import { ScienceSearchCondition } from 'src/app/models/science-search-condition';
import { EnglishSearchCondition } from 'src/app/models/english-search-condition';
import { NationalLanguageSearchCondition } from 'src/app/models/national-language-search-condition';
import { HistorySearchCondition } from 'src/app/models/history-search-condition';
import { User } from 'src/app/models/user';
import { Bookmark, BookmarkProblem } from 'src/app/models/bookmark';
import { Subject } from '../../../models/common-data';
import { SearchBookmarkCondition, SearchBookmarkProblemListCondition } from 'src/app/models/search-bookmark-condition';
import { StaticCommonData } from 'src/app/models/static-common-data';

// Utils
import { Dates } from 'src/app/utils/dates';
import { Log } from 'src/app/utils/log';
import { SubjectUtil } from 'src/app/utils/subject-util';
import { WINDOW_OBJECT } from '../../../utils/injection-tokens';

// Config & Resources
import { fadeInOut, showHide } from 'src/app/resources/animations';
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';
import {
  SubjectId,
  STATIC_DATA_CACHE_DAYS,
  META_VIEWPORT_OTHER,
  SCIENCE_IDS,
  IS_BOTTOM_TOAST_BANNER_VISIBLE,
  ProblemDetailParentComponents
} from 'src/app/resources/config';
import { UAParser } from 'ua-parser-js';

// Error
import { GeneralError } from 'src/app/errors/general-error';
import { CustomErrorMessage } from 'src/app/errors/error-info';
import { FindMyBookmarkProblemListParams, FindSharedBookmarkProblemListParams } from 'src/app/models/bookmark-problem-list';
import { findBookmarkProblemListProblemIds } from 'src/app/actions/bookmark-problem-list.actions';
import { FindBookmarkProblemsParams } from 'src/app/models/bookmark-problem';

type SearchProblemsConditionType =
  | SearchProblemsCondition<ScienceSearchCondition>
  | SearchProblemsCondition<EnglishSearchCondition>
  | SearchProblemsCondition<NationalLanguageSearchCondition>
  | SearchProblemsCondition<HistorySearchCondition>;

@Component({
  selector: 'app-problem-detail',
  templateUrl: './problem-detail.component.html',
  styleUrls: ['./problem-detail.component.scss'],
  animations: [fadeInOut, showHide]
})
export class ProblemDetailComponent implements OnInit, OnDestroy {
  private LOG_SOURCE = this.constructor.name;
  private subscriptions: Subscription[] = [];

  protected readonly isBottomToastBannerVisible = IS_BOTTOM_TOAST_BANNER_VISIBLE;
  protected readonly SubjectId = SubjectId;

  problemId$: Observable<string>;
  private searchProblemsCondition$: Observable<SearchProblemsConditionType>;
  scienceSearchProblemsCondition: SearchProblemsCondition<ScienceSearchCondition>;
  englishSearchProblemsCondition: SearchProblemsCondition<EnglishSearchCondition>;
  nationalLanguageSearchProblemsCondition: SearchProblemsCondition<NationalLanguageSearchCondition>;
  historySearchProblemsCondition: SearchProblemsCondition<HistorySearchCondition>;
  private problemIds$: Observable<string[]>;
  private problemIdsSearching$: Observable<boolean>;
  private problemSearching$: Observable<boolean>;
  private initializedBookmarkSubject = new BehaviorSubject(false);
  private bookmarkSearchCondition$: Observable<SearchBookmarkCondition>;
  private bookmarkProblemIds$: Observable<string[]>;
  private bookmarkProblemListSearchCondition$: Observable<SearchBookmarkProblemListCondition>;
  private bookmarkProblemListProblemIds$: Observable<string[]>;

  scienceIds: string[] = SCIENCE_IDS;

  staticCommonData$: Observable<StaticCommonData>;

  /** science-problems.component をそのまま利用するため array で定義 */
  readableScienceProblems$: Observable<ReadableScienceProblem[]>;
  readableEnglishProblems$: Observable<ReadableEnglishProblem[]>;
  readableNationalLanguageProblems$: Observable<ReadableNationalLanguageProblem[]>;
  readableJapaneseHistoryProblems$: Observable<ReadableHistoryProblem[]>;
  readableWorldHistoryProblems$: Observable<ReadableHistoryProblem[]>;

  problem$: Observable<Problem>;
  scienceProblem$: Observable<ScienceProblem>;
  englishProblem$: Observable<EnglishProblem>;
  nationalLanguageProblem$: Observable<NationalLanguageProblem>;
  nationalLanguageProblems$: Observable<NationalLanguageProblem[]>;
  historyProblem$: Observable<HistoryProblem>;
  readableSortType$: Observable<string>;
  searching$: Observable<boolean>;
  isFirstProblem$: Observable<boolean>;
  isLastProblem$: Observable<boolean>;
  isPreconditionError = false;
  daimonMode$: Observable<boolean>;
  paperMode$: Observable<boolean>;
  trialMode$: Observable<boolean>;
  navigation$: Observable<boolean>;
  subjectId$: Observable<string>;
  menuType$: Observable<'article' | 'spellcheck'> = of('article');
  ENGLISH_SUBJECT_ID: string;
  NATIONAL_LANGUAGE_SUBJECT_ID: string;
  JAPANESE_HISTORY_SUBJECT_ID: string;
  WORLD_HISTORY_SUBJECT_ID: string;

  toolbarTitle = '問題詳細';

  signedInUser$: Observable<User>;
  user: User;
  bookmark: Bookmark;
  bookmarkSubjectProblems: BookmarkProblem[];
  initializedBookmark$ = this.initializedBookmarkSubject.asObservable();
  parentComponent: string;

  isFrom$: Observable<string>;

  constructor(
    private store: Store<RootState>,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private meta: Meta,
    @Inject(WINDOW_OBJECT) private window: Window
  ) {
    this.ENGLISH_SUBJECT_ID = SubjectId.ENGLISH;
    this.NATIONAL_LANGUAGE_SUBJECT_ID = SubjectId.NATIONAL_LANGUAGE;
    this.JAPANESE_HISTORY_SUBJECT_ID = SubjectId.JAPANESE_HISTORY;
    this.WORLD_HISTORY_SUBJECT_ID = SubjectId.WORLD_HISTORY;
  }

  ngOnInit() {
    this.meta.updateTag(META_VIEWPORT_OTHER);
    this.store.dispatch(setBrowserTitle({ subTitle: this.toolbarTitle }));
    this.setUpUser();
    this.setUpStaticData();
    this.setUpTrialMode();
    this.setUpNavigationAndPaperMode();
    this.setUpProblemId();
    this.setUpBookmarkProblemLists();
    this.setUpBookmarkProblems();

    this.setUpBookmarkFlag();
    this.isFrom$.pipe(take(1)).subscribe(isFrom => {
      if (isFrom === 'bookmark-problem-list') {
        // お気に入りリスト問題詳細で必要な処理
        this.parentComponent = ProblemDetailParentComponents.BOOKMARK_PROBLEM_LIST_PROBLEM_DETAIL;
        this.setupBookmarkProblemListSubjectId();
        this.setUpBookmarkProblemListSearchCondition();
        this.setUpBookmarkProblemListProblemIds();
        this.setUpBookmarkProblemListProblemPositionFlags();
        this.setUpBookmarkProblemListReadableSortType();
        this.setUpBookmarkProblemListSearchingFlag();
      } else if (isFrom === 'bookmark') {
        // お気に入り問題詳細で必要な処理
        this.parentComponent = ProblemDetailParentComponents.BOOKMARK_PROBLEM_DETAIL;
        this.setupBookmarkSubjectId();
        this.setUpBookmarkSearchCondition();
        this.setUpBookmarkProblemIds();
        this.setUpBookmarkProblemPositionFlags();
        this.setUpBookmarkReadableSortType();
        this.setUpBookmarkSearchingFlag();
      } else {
        // 基礎検索問題詳細で必要な処理
        this.parentComponent = ProblemDetailParentComponents.SEARCH_PROBLEM_DETAIL;
        this.setupSearchSubjectId();
        this.setUpSearchCondition();
        this.setUpSearchProblemIds();
        this.setUpSearchProblemPositionFlags();
        this.setUpSearchReadableSortType();
        this.setUpSearchSearchingFlag();
      }
    });

    this.setUpProblem();
    this.setUpReadableProblem();
    this.setUpBookmarks();
    this.setUpBrowserTitle();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sb => sb.unsubscribe());
    this.subscriptions = [];
    this.store.dispatch(BookmarkActions.initializeAddBookmarkState());
    this.store.dispatch(BookmarkActions.initializeDeleteBookmarkState());
    this.store.dispatch(BookmarkActions.initializeFindBookmarkProblemIdsState());
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter((user): user is User => user !== null && user !== 'none'));
  }

  private setUpStaticData() {
    this.findStaticDataIfNeeded();

    this.staticCommonData$ = this.store.select(StaticDataSelectors.getStaticCommonData).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpTrialMode() {
    this.trialMode$ = this.store.select(getSignedInUser).pipe(
      filter<User>(it => it != null && it !== 'none'),
      map(signedInUser => (signedInUser.isTrial === true ? true : false))
    );
  }

  private setUpBookmarkFlag() {
    this.isFrom$ = this.activatedRoute.queryParams.pipe(
      map(params => {
        if (Object.prototype.hasOwnProperty.call(params, 'from') && params.from === 'bookmark-problem-list') {
          return 'bookmark-problem-list';
        } else if (Object.prototype.hasOwnProperty.call(params, 'from') && params.from === 'bookmark') {
          return 'bookmark';
        }
        return '';
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpNavigationAndPaperMode() {
    this.navigation$ = of(true);
    this.daimonMode$ = of(true);
    this.paperMode$ = of(false);

    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe(params => {
        if (Object.prototype.hasOwnProperty.call(params, 'mode') && params.mode === 'paper') {
          this.navigation$ = of(false);
          this.daimonMode$ = of(false);
          this.paperMode$ = of(true);
        }
      })
    );
  }

  private setUpProblemId() {
    this.problemId$ = this.activatedRoute.paramMap.pipe(
      map(paramMap => paramMap.get('problemId') || ''),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.subscriptions.push(
      this.problemId$.subscribe(problemId => {
        this.isPreconditionError = problemId === '';
        if (!problemId) {
          const error = GeneralError.customMessage(CustomErrorMessage.PROBLEM_DETAIL_FAILED_PRECONDITION);
          this.store.dispatch(dispatchAppError({ source: this.LOG_SOURCE, error }));
        }
      })
    );
  }

  private setupSearchSubjectId() {
    this.subjectId$ = this.activatedRoute.queryParams.pipe(
      map(queryParams => QueryParamsMapper.decodeSearchProblemsConditionQueryParams(queryParams)),
      // map(condition => condition as SearchProblemsCondition<ScienceSearchCondition>)
      map(condition => condition.subjectId)
    );
  }

  private setupBookmarkSubjectId() {
    this.signedInUser$.pipe(take(1)).subscribe(user => {
      this.subjectId$ = this.activatedRoute.queryParams.pipe(
        map(queryParams => QueryParamsMapper.decodeBookmarkSearchProblemsCondition(queryParams, user.id)),
        map(condition => condition.subjectId)
      );
    });
  }

  private setupBookmarkProblemListSubjectId() {
    this.signedInUser$.pipe(take(1)).subscribe(user => {
      this.subjectId$ = this.activatedRoute.queryParams.pipe(
        map(queryParams => QueryParamsMapper.decodeBookmarkProblemListSearchProblemsCondition(queryParams, user.id)),
        map(condition => condition.subjectId)
      );
    });
  }

  private setUpSearchCondition() {
    this.searchProblemsCondition$ = this.activatedRoute.queryParams.pipe(
      map(queryParams => QueryParamsMapper.decodeSearchProblemsConditionQueryParams(queryParams)),
      // map(condition => condition as SearchProblemsCondition<ScienceSearchCondition>)
      map(condition => {
        if (this.scienceIds.includes(condition.subjectId)) {
          const _condition = condition as SearchProblemsCondition<ScienceSearchCondition>;
          this.scienceSearchProblemsCondition = _condition;
          return _condition;
        } else if (condition.subjectId === SubjectId.ENGLISH) {
          const _condition = condition as SearchProblemsCondition<EnglishSearchCondition>;
          this.englishSearchProblemsCondition = _condition;
          return _condition;
        } else if (condition.subjectId === SubjectId.NATIONAL_LANGUAGE) {
          const _condition = condition as SearchProblemsCondition<NationalLanguageSearchCondition>;
          this.nationalLanguageSearchProblemsCondition = _condition;
          return _condition;
        } else if (condition.subjectId === SubjectId.JAPANESE_HISTORY || condition.subjectId === SubjectId.WORLD_HISTORY) {
          const _condition = condition as SearchProblemsCondition<HistorySearchCondition>;
          this.historySearchProblemsCondition = _condition;
          return _condition;
        }
      })
    );
  }

  private setUpBookmarkSearchCondition() {
    this.signedInUser$.pipe(take(1)).subscribe(user => {
      this.bookmarkSearchCondition$ = this.activatedRoute.queryParams.pipe(
        map(queryParams => QueryParamsMapper.decodeBookmarkSearchProblemsCondition(queryParams, user.id))
      );
    });
  }

  private setUpBookmarkProblemIds() {
    this.bookmarkProblemIds$ = this.store.select(BookmarkSelectors.getMatchedBookmarkProblemIds).pipe(filter(it => it != null));
    this.subscriptions.push(
      this.bookmarkSearchCondition$.subscribe(searchCondition => {
        const condition: SearchBookmarkCondition = searchCondition;
        this.store.dispatch(findBookmarkProblemIds({ condition }));
      })
    );
  }

  private setUpBookmarkProblemListSearchCondition() {
    this.signedInUser$.pipe(take(1)).subscribe(user => {
      this.bookmarkProblemListSearchCondition$ = this.activatedRoute.queryParams.pipe(
        map(queryParams => QueryParamsMapper.decodeBookmarkProblemListSearchProblemsCondition(queryParams, user.id))
      );
    });
  }

  private setUpBookmarkProblemListProblemIds() {
    this.bookmarkProblemListProblemIds$ = this.store
      .select(BookmarkProblemListSelectors.getMatchedBookmarkProblemListProblemIds)
      .pipe(filter(it => it != null));
    this.subscriptions.push(
      this.bookmarkProblemListSearchCondition$.subscribe(searchCondition => {
        const condition: SearchBookmarkProblemListCondition = searchCondition;
        this.store.dispatch(findBookmarkProblemListProblemIds({ bookmarkProblemListId: condition.bookmarkProblemListId }));
      })
    );
  }

  private setUpSearchProblemIds() {
    this.problemIds$ = this.store.select(getMatchedProblemIds).pipe(filter(it => it != null));
    this.subscriptions.push(
      this.searchProblemsCondition$.subscribe(condition => {
        // フリーワード検索の場合
        if (condition.freeword != null) {
          this.store.dispatch(findProblemIdsByFreeword({ condition }));
          return;
        }
        // フリーワード検索以外の場合
        if (this.scienceIds.includes(condition.subjectId)) {
          this.store.dispatch(findScienceProblemIds({ condition: this.scienceSearchProblemsCondition }));
        } else if (condition.subjectId === SubjectId.ENGLISH) {
          this.store.dispatch(findEnglishProblemIds({ condition: this.englishSearchProblemsCondition }));
        } else if (condition.subjectId === SubjectId.NATIONAL_LANGUAGE) {
          this.store.dispatch(
            findNationalLanguageProblemIds({
              condition: this.nationalLanguageSearchProblemsCondition
            })
          );
        } else if (condition.subjectId === SubjectId.JAPANESE_HISTORY || condition.subjectId === SubjectId.WORLD_HISTORY) {
          this.store.dispatch(findHistoryProblemIds({ condition: this.historySearchProblemsCondition }));
        }
      })
    );
  }

  private setUpProblem() {
    this.subjectId$.pipe(take(1)).subscribe(subjectId => {
      if (this.scienceIds.includes(subjectId)) {
        this.scienceProblem$ = this.store.select(getScienceProblem).pipe(
          filter(it => it != null),
          shareReplay({ bufferSize: 1, refCount: true })
        );
        this.problem$ = this.scienceProblem$.pipe(problem => problem);
      } else if (subjectId === SubjectId.ENGLISH) {
        this.englishProblem$ = this.store.select(getEnglishProblem).pipe(
          filter(it => it != null),
          shareReplay({ bufferSize: 1, refCount: true })
        );
        this.problem$ = this.englishProblem$.pipe(problem => problem);
      } else if (subjectId === SubjectId.NATIONAL_LANGUAGE) {
        this.nationalLanguageProblems$ = this.store.select(getNationalLanguageProblemsWithSameId).pipe(
          filter(it => it != null),
          shareReplay({ bufferSize: 1, refCount: true }),
          map(problems => problems)
        );
        this.problem$ = this.nationalLanguageProblems$.pipe(map(problems => problems[0]));
      } else if (subjectId === SubjectId.JAPANESE_HISTORY || subjectId === SubjectId.WORLD_HISTORY) {
        this.historyProblem$ = this.store.select(getHistoryProblem).pipe(
          filter(it => it != null),
          shareReplay({ bufferSize: 1, refCount: true })
        );
        this.problem$ = this.historyProblem$.pipe(problem => problem);
      }
    });

    this.subscriptions.push(
      combineLatest([this.problemId$.pipe(filter(it => it !== '')), this.subjectId$, this.isFrom$])
        .pipe(
          switchMap(([problemId, subjectId, isFrom]) => {
            const ids$ =
              isFrom === 'bookmark-problem-list'
                ? this.bookmarkProblemListProblemIds$
                : isFrom === 'bookmark'
                ? this.bookmarkProblemIds$
                : this.problemIds$;
            return ids$.pipe(
              take(1),
              map(ids => ({ problemId, subjectId, ids }))
            );
          })
        )
        .subscribe(({ problemId, subjectId, ids }) => {
          // URLの問題IDが正しい値かどうかをチェック - 正しい場合のみAPIの呼び出し
          if (!ids.includes(problemId)) {
            this.setUpProblemIdError();
            return;
          }
          this.isPreconditionError = false;

          // 問題を検索するAPIを呼び出す
          if (this.scienceIds.includes(subjectId)) {
            this.store.dispatch(findScienceProblem({ subjectId, problemId }));
          } else if (subjectId === SubjectId.ENGLISH) {
            this.store.dispatch(findEnglishProblem({ subjectId, problemId }));
          } else if (subjectId === SubjectId.NATIONAL_LANGUAGE) {
            this.store.dispatch(findNationalLanguageProblemsById({ subjectId, problemId }));
          } else if (subjectId === SubjectId.JAPANESE_HISTORY || subjectId === SubjectId.WORLD_HISTORY) {
            this.store.dispatch(findHistoryProblem({ subjectId, problemId }));
          }
        })
    );
  }

  private setUpProblemIdError() {
    this.isPreconditionError = true;
    const error = GeneralError.customMessage(CustomErrorMessage.PROBLEM_DETAIL_FAILED_PRECONDITION);
    this.store.dispatch(dispatchAppError({ source: this.LOG_SOURCE, error }));
  }

  private setUpSearchReadableSortType() {
    this.readableSortType$ = this.searchProblemsCondition$.pipe(
      map(condition => ReadableDataMapper.getSortTypeDisplayName(condition.sortType))
    );
  }

  private setUpBookmarkReadableSortType() {
    this.readableSortType$ = this.bookmarkSearchCondition$.pipe(
      map(condition => ReadableDataMapper.getBookmarkSortTypeDisplayName(condition.sortType))
    );
  }

  private setUpBookmarkProblemListReadableSortType() {
    this.readableSortType$ = of('');
  }

  private setUpReadableProblem() {
    this.subjectId$.pipe(take(1)).subscribe(subjectId => {
      if (this.scienceIds.includes(subjectId)) {
        this.readableScienceProblems$ = combineLatest([this.scienceProblem$]).pipe(
          switchMap(([problem]) => {
            const selector =
              subjectId === SubjectId.MATH
                ? this.store.select(getReadableMathProblem(problem))
                : subjectId === SubjectId.PHYSICS
                ? this.store.select(getReadablePhysicsProblem(problem))
                : subjectId === SubjectId.CHEMISTRY
                ? this.store.select(getReadableChemistryProblem(problem))
                : subjectId === SubjectId.BIOLOGY
                ? this.store.select(getReadableBiologyProblem(problem))
                : subjectId === SubjectId.GEOGRAPHY
                ? this.store.select(getReadableGeographyProblem(problem))
                : subjectId === SubjectId.POLITICAL_ECONOMY
                ? this.store.select(getReadablePoliticalEconomyProblem(problem))
                : this.store.select(getReadableSpecialAdmissionsProblem(problem));
            return selector;
          }),
          map(problem => [problem]),
          filter(it => it != null)
        );
      } else if (subjectId === SubjectId.ENGLISH) {
        this.readableEnglishProblems$ = combineLatest([this.englishProblem$]).pipe(
          switchMap(([problem]) => {
            const selector = this.store.select(getReadableEnglishProblem(problem));
            return selector;
          }),
          map(problem => [problem]),
          filter(it => it != null)
        );
      } else if (subjectId === SubjectId.NATIONAL_LANGUAGE) {
        this.readableNationalLanguageProblems$ = combineLatest([this.nationalLanguageProblems$]).pipe(
          switchMap(([problems]) => {
            const selector = this.store.select(getReadableNationalLanguageProblemsFromProblems(problems));
            return selector;
          }),
          map(problems => problems),
          filter(it => it != null)
        );
      } else if (subjectId === SubjectId.JAPANESE_HISTORY) {
        this.readableJapaneseHistoryProblems$ = combineLatest([this.historyProblem$]).pipe(
          switchMap(([problem]) => {
            const selector = this.store.select(getReadableJapaneseHistoryProblem(problem));
            return selector;
          }),
          map(problem => [problem]),
          filter(it => it != null)
        );
      } else if (subjectId === SubjectId.WORLD_HISTORY) {
        this.readableWorldHistoryProblems$ = combineLatest([this.historyProblem$]).pipe(
          switchMap(([problem]) => {
            const selector = this.store.select(getReadableWorldHistoryProblem(problem));
            return selector;
          }),
          map(problem => [problem]),
          filter(it => it != null)
        );
      }
    });
  }

  private setUpSearchSearchingFlag() {
    this.problemIdsSearching$ = this.store.select(getProblemIdsSearching).pipe(startWith(true));
    this.problemSearching$ = this.store.select(getProblemSearching).pipe(startWith(true));
    this.searching$ = combineLatest([this.problemIdsSearching$, this.problemSearching$]).pipe(
      map(([idsSearching, problemSearching]) => idsSearching || problemSearching)
    );
  }

  private setUpBookmarkSearchingFlag() {
    this.problemIdsSearching$ = this.store.select(BookmarkSelectors.getBookmarkProblemIdsSearching).pipe(startWith(true));
    this.problemSearching$ = this.store.select(getProblemSearching).pipe(startWith(true));
    this.searching$ = combineLatest([this.problemIdsSearching$, this.problemSearching$]).pipe(
      map(([idsSearching, problemSearching]) => idsSearching || problemSearching)
    );
  }

  private setUpBookmarkProblemListSearchingFlag() {
    this.problemIdsSearching$ = this.store
      .select(BookmarkProblemListSelectors.getBookmarkProblemListProblemIdsSearching)
      .pipe(startWith(true));
    this.problemSearching$ = this.store.select(getProblemSearching).pipe(startWith(true));
    this.searching$ = combineLatest([this.problemIdsSearching$, this.problemSearching$]).pipe(
      map(([idsSearching, problemSearching]) => idsSearching || problemSearching)
    );
  }

  private setUpSearchProblemPositionFlags() {
    const problemId$ = this.problemId$.pipe(filter(it => it !== ''));
    this.isFirstProblem$ = combineLatest([
      problemId$,
      this.store.select(getFirstProblemIdFromMatchedProblemIds).pipe(filter(it => it != null))
    ]).pipe(map(([problemId, firstProblemId]) => problemId === firstProblemId));

    this.isLastProblem$ = combineLatest([
      problemId$,
      this.store.select(getLastProblemIdFromMatchedProblemIds).pipe(filter(it => it != null))
    ]).pipe(map(([problemId, lastProblemId]) => problemId === lastProblemId));
  }

  private setUpBookmarkProblemPositionFlags() {
    const problemId$ = this.problemId$.pipe(filter(it => it !== ''));
    this.isFirstProblem$ = combineLatest([
      problemId$,
      this.store.select(BookmarkSelectors.getFirstProblemIdFromMatchedBookmarkProblemIds).pipe(filter(it => it != null))
    ]).pipe(map(([problemId, firstProblemId]) => problemId === firstProblemId));

    this.isLastProblem$ = combineLatest([
      problemId$,
      this.store.select(BookmarkSelectors.getLastProblemIdFromMatchedBookmarkProblemIds).pipe(filter(it => it != null))
    ]).pipe(map(([problemId, lastProblemId]) => problemId === lastProblemId));
  }

  private setUpBookmarkProblemListProblemPositionFlags() {
    const problemId$ = this.problemId$.pipe(filter(it => it !== ''));
    this.isFirstProblem$ = combineLatest([
      problemId$,
      this.store
        .select(BookmarkProblemListSelectors.getFirstProblemIdFromMatchedBookmarkProblemListProblemIds)
        .pipe(filter(it => it != null))
    ]).pipe(map(([problemId, firstProblemId]) => problemId === firstProblemId));

    this.isLastProblem$ = combineLatest([
      problemId$,
      this.store
        .select(BookmarkProblemListSelectors.getLastProblemIdFromMatchedBookmarkProblemListProblemIds)
        .pipe(filter(it => it != null))
    ]).pipe(map(([problemId, lastProblemId]) => problemId === lastProblemId));
  }

  private setUpBookmarks() {
    const conditionParams = this.activatedRoute.snapshot.queryParams;
    this.store.dispatch(BookmarkActions.initializeFindBookmarkState());
    this.store
      .select(getSignedInUser)
      .pipe(filter<User>(it => it != null && it !== 'none'))
      .pipe(take(1))
      .subscribe(user => {
        this.user = user;
        this.store.dispatch(BookmarkActions.initializeFindBookmarkState());
        this.store.dispatch(BookmarkActions.findBookmark({ userId: user.id }));
        this.store
          .select(BookmarkSelectors.getBookmark)
          .pipe(
            filter(it => it != null),
            take(1)
          )
          .subscribe(bookmark => {
            this.bookmark = bookmark;
            this.bookmarkSubjectProblems = [];
            if (bookmark.problems !== undefined) {
              this.bookmarkSubjectProblems = this.bookmark.problems.filter(problem => problem.subjectId === conditionParams.subjectId);
            }
            this.store.dispatch(BookmarkActions.initializeFindBookmarkState());

            this.initializedBookmarkSubject.next(true);
          });
      });
  }

  private setUpBrowserTitle(menuType = 'article') {
    const subjects$: Observable<Subject[]> = this.store.select(StaticDataSelectors.getSubject).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscriptions.push(
      this.subjectId$
        .pipe(
          take(1),
          switchMap(subjectId => {
            let readableProblems$: Observable<
              ReadableScienceProblem[] | ReadableEnglishProblem[] | ReadableNationalLanguageProblem[] | ReadableHistoryProblem[]
            >;
            if (this.scienceIds.includes(subjectId)) {
              readableProblems$ = this.readableScienceProblems$;
            } else if (subjectId === SubjectId.ENGLISH) {
              readableProblems$ = this.readableEnglishProblems$;
            } else if (subjectId === SubjectId.NATIONAL_LANGUAGE) {
              readableProblems$ = this.readableNationalLanguageProblems$;
            } else if (subjectId === SubjectId.JAPANESE_HISTORY) {
              readableProblems$ = this.readableJapaneseHistoryProblems$;
            } else if (subjectId === SubjectId.WORLD_HISTORY) {
              readableProblems$ = this.readableWorldHistoryProblems$;
            }

            if (!readableProblems$) {
              return EMPTY;
            }

            return combineLatest([subjects$, readableProblems$.pipe(filter(it => it !== null))]).pipe(
              map(([subjects, problems]) => ({ subjects, problems, subjectId }))
            );
          })
        )
        .subscribe(({ subjects, problems, subjectId }) => {
          const university: string = problems[0].university;
          const year: number = problems[0].year;
          const subject: string = SubjectUtil.getName(subjects, subjectId);
          const type = menuType === 'article' ? '問題' : '研究・解答';
          const title = `${university}　${year}年度　${subject}　${type}`;
          this.store.dispatch(setBrowserTitle({ subTitle: `${title}`, problemDetailFlag: true }));
        })
    );
  }

  private findStaticDataIfNeeded() {
    this.store
      .select(getFetchedDate)
      .pipe(take(1))
      .subscribe(fetchedDate => {
        if (!fetchedDate) {
          Log.debug(this.LOG_SOURCE, `static data が存在していないため取得します`);
          this.store.dispatch(findStaticData());
          return;
        }
        const cacheExpired = Dates.isCachedDateExpired(fetchedDate, STATIC_DATA_CACHE_DAYS);
        if (cacheExpired) {
          Log.debug(this.LOG_SOURCE, `cache 期間を超過したため再度 static data を取得します. fetchedDate: ${fetchedDate}`);
          this.store.dispatch(initializeStaticDataState());
          this.store.dispatch(findStaticData());
          return;
        }
        Log.debug(this.LOG_SOURCE, 'static data が取得済みのため何もしません');
      });
  }

  showPaper() {
    this.problemId$.pipe(take(1)).subscribe(problemId => {
      const { queryParams } = this.activatedRoute.snapshot;
      const url = RoutingPathResolver.resolveProblemDetail(problemId);
      this.store.dispatch(openWindow({ url, extras: { queryParams: { ...queryParams, mode: 'paper' } } }));
    });
  }

  showPrint() {
    const ua = new UAParser(this.window.navigator.userAgent).getResult();
    if (ua.browser.name.indexOf('Safari') > -1) {
      document.execCommand('print', false, null);
    } else {
      (window as any).print();
    }
  }

  changeMenuType(event) {
    this.menuType$ = of(event);
    this.menuType$.pipe(take(1)).subscribe(menuType => this.setUpBrowserTitle(menuType));
  }

  private setUpBookmarkProblemLists() {
    this.store.dispatch(BookmarkProblemListActions.initializeFindMyBookmarkProblemListsState());
    this.store.dispatch(BookmarkProblemListActions.initializeFindSharedBookmarkProblemListsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findMyBookmarkProblemListParams: FindMyBookmarkProblemListParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemListActions.findMyBookmarkProblemLists(findMyBookmarkProblemListParams));

        const findSharedBookmarkProblemListParams: FindSharedBookmarkProblemListParams = {
          schoolId: signedInUser.schoolId,
          userId: signedInUser.id
        };
        this.store.dispatch(BookmarkProblemListActions.findSharedBookmarkProblemLists(findSharedBookmarkProblemListParams));
      });
  }

  private setUpBookmarkProblems() {
    this.store.dispatch(BookmarkProblemsActions.initializeFindBookmarkProblemsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findBookmarkProblemsParams: FindBookmarkProblemsParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemsActions.findBookmarkProblems(findBookmarkProblemsParams));
      });
  }
}
